import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { WorkspaceInstance } from '@crowdctrl/core';
import { WorkspaceInstanceService } from '@crowdctrl/shared';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class WorkspaceContextService {
  private workspaceInstanceService = inject(WorkspaceInstanceService);

  private workspaceSubject = new BehaviorSubject<WorkspaceInstance | null>(null);
  private workspaceIdSubject = new BehaviorSubject<string | null>(null);
  private loading$: Observable<WorkspaceInstance | null> | null = null;

  workspace$ = this.workspaceSubject.asObservable();
  workspaceId$ = this.workspaceIdSubject.asObservable();

  getWorkspaceValue(): WorkspaceInstance | null {
    return this.workspaceSubject.value;
  }

  getWorkspaceId(): string | null {
    return this.workspaceIdSubject.value;
  }

  ensureWorkspace$(
    workspaceId: string,
    forceRefresh: boolean = false,
  ): Observable<WorkspaceInstance | null> {
    if (this.workspaceIdSubject.value !== workspaceId) {
      this.workspaceSubject.next(null);
    }

    if (
      !forceRefresh &&
      this.workspaceIdSubject.value === workspaceId &&
      this.workspaceSubject.value
    ) {
      return of(this.workspaceSubject.value);
    }

    if (!forceRefresh && this.workspaceIdSubject.value === workspaceId && this.loading$) {
      return this.loading$;
    }

    this.workspaceIdSubject.next(workspaceId);

    const request$ = this.workspaceInstanceService.getInstance(workspaceId).pipe(
      map((response) => response?.instance || null),
      tap((instance) => {
        this.workspaceSubject.next(instance);
      }),
      finalize(() => {
        if (this.loading$ === request$) {
          this.loading$ = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.loading$ = request$;
    return request$;
  }

  async ensureWorkspace(
    workspaceId: string,
    forceRefresh: boolean = false,
  ): Promise<WorkspaceInstance | null> {
    return await firstValueFrom(this.ensureWorkspace$(workspaceId, forceRefresh));
  }

  setWorkspace(instance: WorkspaceInstance | null): void {
    this.workspaceSubject.next(instance);
    if (instance?.id) {
      this.workspaceIdSubject.next(instance.id);
    }
  }

  clear(): void {
    this.workspaceSubject.next(null);
    this.workspaceIdSubject.next(null);
    this.loading$ = null;
  }
}
